// Shared helpers for the FOB calendar (customer dashboard) and the FOB growth
// chart (Insights). Shipments are dated by their first S/B date and all of
// their FOB / RoDTEP / DBK / IGST / RoSCTL values are summed onto that day.

const MONTH_INDEX = {
  JAN: 0, FEB: 1, MAR: 2, APR: 3, MAY: 4, JUN: 5,
  JUL: 6, AUG: 7, SEP: 8, OCT: 9, NOV: 10, DEC: 11
};

const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export const AMOUNT_FIELDS = ['fob', 'rodtep', 'dbk', 'igst', 'rosctl'];

const normalizeYear = (y) => {
  let year = Number(y);
  // "25" -> 2025, "025" -> 2025
  if (year < 1000) year += 2000;
  return year;
};

const buildDate = (year, monthIndex, day) => {
  const date = new Date(normalizeYear(year), monthIndex, Number(day));
  return Number.isNaN(date.getTime()) ? null : date;
};

const firstValue = (value) => {
  if (value === null || value === undefined) return null;
  if (Array.isArray(value)) {
    const found = value.find((v) => v !== null && v !== undefined && String(v).trim() !== '' && String(v).trim() !== '-');
    return found === undefined ? null : found;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed.startsWith('[')) {
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) return firstValue(parsed);
      } catch {
        // fall through to plain string handling
      }
    }
    return trimmed;
  }
  return value;
};

// Parses the first date out of a S/B date field. Supports arrays, JSON array
// strings, "a & b" joined strings, DD/MM/YYYY, DD-MM-YY, DD-MMM-YY(YY),
// DD MMM YYYY, YYYY-MM-DD, YYYY/MM/DD and ISO timestamps.
export const parseShipmentDate = (value) => {
  const raw = firstValue(value);
  if (raw === null || raw === undefined || raw === '') return null;
  if (raw instanceof Date) return Number.isNaN(raw.getTime()) ? null : raw;
  if (typeof raw !== 'string') return null;

  const first = raw.split('&')[0].trim();
  if (!first || first === '-') return null;

  const iso = first.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})(?:[T\s].*)?$/);
  if (iso) return buildDate(iso[1], Number(iso[2]) - 1, iso[3]);

  const datePart = first.split(/[\sT]/)[0];

  const numeric = datePart.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{2,4})$/);
  if (numeric) return buildDate(numeric[3], Number(numeric[2]) - 1, numeric[1]);

  const named = first.match(/^(\d{1,2})[-\s]([A-Za-z]{3})[A-Za-z]*[-\s,]+(\d{2,4})/);
  if (named) {
    const monthIndex = MONTH_INDEX[named[2].toUpperCase()];
    if (monthIndex !== undefined) return buildDate(named[3], monthIndex, named[1]);
  }

  const parsed = new Date(first);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

// Sums every amount in a field: numbers, arrays, JSON array strings,
// "₹1,200 & ₹300" style strings. Missing / "-" values count as 0.
export const parseAmount = (value) => {
  if (value === null || value === undefined || value === '') return 0;
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;

  let values;
  if (Array.isArray(value)) {
    values = value;
  } else if (typeof value === 'string') {
    const trimmed = value.trim();
    values = null;
    if (trimmed.startsWith('[')) {
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) values = parsed;
      } catch {
        // fall through to regex extraction
      }
    }
    if (!values) values = trimmed.match(/-?\d[\d,]*(?:\.\d+)?/g) || [];
  } else {
    values = [value];
  }

  return values.reduce((sum, item) => {
    if (item === null || item === undefined || item === '') return sum;
    if (Array.isArray(item)) return sum + parseAmount(item);
    const num = Number(String(item).replace(/[₹,\s]/g, ''));
    return sum + (Number.isFinite(num) ? num : 0);
  }, 0);
};

const pad = (n) => String(n).padStart(2, '0');

export const toDateKey = (date) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
export const toMonthKey = (date) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}`;
export const toYearKey = (date) => String(date.getFullYear());

const joinValues = (value) => {
  if (value === null || value === undefined || value === '') return '';
  if (Array.isArray(value)) {
    return [...new Set(value.filter((v) => v !== null && v !== undefined && v !== '').map(String))].join(' & ');
  }
  return String(value);
};

const pick = (shipment, keys) => {
  for (const key of keys) {
    const v = shipment[key];
    if (v !== null && v !== undefined && v !== '' && v !== '-') return v;
  }
  return undefined;
};

const withDate = (base, rawDate) => {
  const date = parseShipmentDate(rawDate);
  return {
    ...base,
    date,
    dateKey: date ? toDateKey(date) : null
  };
};

// Raw air-export record (GET_USER_SHIPMENTS) -> calendar item.
export const normalizeAirShipment = (shipment = {}) => withDate({
  id: shipment.id ?? shipment.awbNumber ?? shipment.awbNo,
  refNo: shipment.awbNumber || shipment.awbNo || shipment.awb_no || '',
  sbNo: joinValues(pick(shipment, ['sbNumbers', 'sbNo', 'sb_no'])),
  customer: shipment.customerName || shipment.username || shipment.customer || '',
  buyer: shipment.buyerName || '',
  country: shipment.destCountry || shipment.country || '',
  transportType: 'air',
  fob: parseAmount(pick(shipment, ['sbFobValues', 'fobValue', 'fob'])),
  rodtep: parseAmount(pick(shipment, ['Rodtep', 'rodtep', 'rodtepAmt'])),
  dbk: parseAmount(pick(shipment, ['DBK', 'dbk', 'dbkClaim'])),
  igst: parseAmount(pick(shipment, ['IGST', 'igst', 'igstAmt'])),
  rosctl: parseAmount(pick(shipment, ['Rosctl', 'rosctl', 'rosctlAmt']))
}, pick(shipment, ['sbDates', 'sbDate', 'sb_date']));

// Raw sea-export record (/sea-export/user/:id) -> calendar item.
export const normalizeSeaShipment = (shipment = {}) => withDate({
  id: shipment.id ?? shipment.bl_number ?? shipment.blNumber,
  refNo: shipment.bl_number || shipment.blNumber || '',
  sbNo: joinValues(pick(shipment, ['sb_no', 'sbNo'])),
  customer: shipment.exporter_name || shipment.exporterName || shipment.consignee_name || shipment.consigneeName || '',
  buyer: shipment.buyer_name || shipment.buyerName || '',
  country: shipment.country_of_final_destination || shipment.countryOfFinalDestination || '',
  transportType: 'sea',
  fob: parseAmount(pick(shipment, ['fob_value', 'fobValue'])),
  rodtep: parseAmount(pick(shipment, ['rodtep_amt', 'rodtepAmt'])),
  dbk: parseAmount(pick(shipment, ['dbk_claim', 'dbkClaim'])),
  igst: parseAmount(pick(shipment, ['igst_amt', 'igstAmt', 'IGST', 'igst'])),
  rosctl: parseAmount(pick(shipment, ['rosctl_amt', 'rosctlAmt']))
}, pick(shipment, ['sbDates', 'sb_date', 'sbDate', 'leo_date', 'leoDate']));

export const normalizeShipments = (shipments, transportType) => {
  if (!Array.isArray(shipments)) return [];
  const normalize = transportType === 'sea' ? normalizeSeaShipment : normalizeAirShipment;
  return shipments.map(normalize);
};

const emptyTotals = () => ({ fob: 0, rodtep: 0, dbk: 0, igst: 0, rosctl: 0, count: 0 });

const addToTotals = (totals, item) => {
  AMOUNT_FIELDS.forEach((field) => { totals[field] += item[field] || 0; });
  totals.count += 1;
  return totals;
};

// { 'YYYY-MM-DD': { dateKey, date, fob, rodtep, dbk, igst, rosctl, count, shipments } }
// Items without a parseable date are skipped.
export const aggregateByDay = (items) => {
  const days = {};
  (items || []).forEach((item) => {
    if (!item || !item.date || !item.dateKey) return;
    if (!days[item.dateKey]) {
      const d = item.date;
      days[item.dateKey] = {
        ...emptyTotals(),
        dateKey: item.dateKey,
        date: new Date(d.getFullYear(), d.getMonth(), d.getDate()),
        shipments: []
      };
    }
    addToTotals(days[item.dateKey], item);
    days[item.dateKey].shipments.push(item);
  });
  return days;
};

// Month grid for the calendar. `monthIndex` is 0-based. Weeks run Sun–Sat;
// every week has 7 slots, slots outside the month are null.
export const buildMonthCalendar = (items, year, monthIndex, dayMap) => {
  const days = dayMap || aggregateByDay(items);
  const firstWeekday = new Date(year, monthIndex, 1).getDay();
  const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();
  const totalSlots = Math.ceil((firstWeekday + daysInMonth) / 7) * 7;

  const monthTotals = { ...emptyTotals(), activeDays: 0 };
  const weeks = [];

  for (let slot = 0; slot < totalSlots; slot += 1) {
    const weekIndex = Math.floor(slot / 7);
    if (!weeks[weekIndex]) {
      weeks[weekIndex] = { index: weekIndex + 1, days: [], totals: { ...emptyTotals(), activeDays: 0 } };
    }
    const week = weeks[weekIndex];
    const dayNumber = slot - firstWeekday + 1;

    if (dayNumber < 1 || dayNumber > daysInMonth) {
      week.days.push(null);
      continue;
    }

    const date = new Date(year, monthIndex, dayNumber);
    const dateKey = toDateKey(date);
    const data = days[dateKey] || null;
    week.days.push({ day: dayNumber, date, dateKey, data });

    if (data) {
      [week.totals, monthTotals].forEach((totals) => {
        AMOUNT_FIELDS.forEach((field) => { totals[field] += data[field]; });
        totals.count += data.count;
        totals.activeDays += 1;
      });
    }
  }

  return { year, monthIndex, weeks, totals: monthTotals };
};

const periodLabel = (date, period) => {
  if (period === 'year') return String(date.getFullYear());
  if (period === 'month') return `${MONTH_SHORT[date.getMonth()]} ${date.getFullYear()}`;
  return `${pad(date.getDate())} ${MONTH_SHORT[date.getMonth()]} ${String(date.getFullYear()).slice(-2)}`;
};

// Growth series for the Insights chart. `period` is 'day' | 'month' | 'year'.
// Returns points sorted oldest -> newest; `growth` is % change vs the previous
// point (null for the first point or when the previous FOB was 0).
export const aggregateByPeriod = (items, period = 'month') => {
  const keyFn = period === 'year' ? toYearKey : period === 'day' ? toDateKey : toMonthKey;
  const buckets = {};

  (items || []).forEach((item) => {
    if (!item || !item.date) return;
    const key = keyFn(item.date);
    if (!buckets[key]) {
      buckets[key] = { ...emptyTotals(), key, label: periodLabel(item.date, period) };
    }
    addToTotals(buckets[key], item);
  });

  const points = Object.values(buckets).sort((a, b) => (a.key < b.key ? -1 : a.key > b.key ? 1 : 0));
  return points.map((point, i) => {
    const prev = i > 0 ? points[i - 1].fob : null;
    const growth = prev ? ((point.fob - prev) / Math.abs(prev)) * 100 : null;
    return { ...point, growth };
  });
};

const formatIN = (value) => new Intl.NumberFormat('en-IN').format(Math.round(value || 0));

// ₹1.2Cr / ₹3.4L / ₹5.6K / ₹950 (same scale as the Insights page)
export const formatShortCurrency = (value) => {
  const amount = Number(value || 0);
  const abs = Math.abs(amount);
  const sign = amount < 0 ? '-' : '';
  if (abs >= 1e7) return `${sign}₹${(abs / 1e7).toFixed(1)}Cr`;
  if (abs >= 1e5) return `${sign}₹${(abs / 1e5).toFixed(1)}L`;
  if (abs >= 1e3) return `${sign}₹${(abs / 1e3).toFixed(1)}K`;
  return `${sign}₹${formatIN(abs)}`;
};

export const formatCurrency = (value) => `₹${formatIN(value)}`;
